import { useId, type Dispatch, type RefObject, type SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import { ResponsiveDialog, Button, Input, Label, Badge, Alert } from '../../components/ui';
import type { BillingPocket, VerificationBill } from '../../api/types';
import { useCoverageGuard } from './useCoverageGuard';
import { CoverageBlockNotice, CoverageUnavailableNotice } from './CoverageBlockNotice';

// "Why did this row match" annotation for a pocket that only matched the
// search via a past tenant name or an embedded meter, not the pocket's own
// name/shop_no. Structurally identical to (but independent of) the
// `RowMatch` type in BillRuns.tsx — kept local since this is purely a
// display concern of this dialog.
type PocketMatch = null | { kind: 'past_tenant' | 'meter'; label: string };

interface PocketGroup {
  name: string;
  rows: BillingPocket[];
}

interface CaOutOfSync {
  buildingName: string;
  caPocket: BillingPocket;
  latestPeriod: { start: string; end: string } | null;
}

interface CreateBillRunDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creating: boolean;
  error: string | null;
  onSubmit: () => void;

  // Verification mode
  verificationMode: boolean;
  onVerificationModeChange: (checked: boolean) => void;

  // Pocket selection + search
  pockets: BillingPocket[];
  loadingPockets: boolean;
  pocketSearch: string;
  onPocketSearchChange: (value: string) => void;
  searchPockets: boolean;
  searchTenants: boolean;
  searchMeters: boolean;
  searchBuildings: boolean;
  setSearchPockets: Dispatch<SetStateAction<boolean>>;
  setSearchTenants: Dispatch<SetStateAction<boolean>>;
  setSearchMeters: Dispatch<SetStateAction<boolean>>;
  setSearchBuildings: Dispatch<SetStateAction<boolean>>;
  filteredPockets: BillingPocket[];
  groupedArr: Array<[string, PocketGroup]>;
  matchByPocket: Map<string, PocketMatch>;
  selectedPocketIds: Set<string>;
  onTogglePocket: (id: string) => void;
  onToggleAllPockets: () => void;
  onToggleGroup: (groupIds: string[], allSelected: boolean) => void;
  totalSelectedMeters: number;

  // CA out-of-sync warning
  caOutOfSync: CaOutOfSync[];
  autoIncludeCa: boolean;
  onAutoIncludeCaChange: (checked: boolean) => void;

  // Period
  periodStart: string;
  onPeriodStartChange: (value: string) => void;
  periodEnd: string;
  onPeriodEndChange: (value: string) => void;

  // Partial-month prorata (Eskom Schedule §6)
  partialMonthProrata: boolean;
  onPartialMonthProrataChange: (checked: boolean) => void;

  // Email on finalize
  emailOnFinalize: boolean;
  onEmailOnFinalizeChange: (checked: boolean) => void;
  emailRecipient: string;
  onEmailRecipientChange: (value: string) => void;

  // Bill verification (council-bill upload + review)
  extracting: boolean;
  extractionProgress: string;
  reviewStep: boolean;
  verificationBill: VerificationBill | null;
  onReupload: () => void;
  fileInputRef: RefObject<HTMLInputElement>;
  onFileSelected: (file: File) => void;
}

export default function CreateBillRunDialog({
  open,
  onOpenChange,
  creating,
  error,
  onSubmit,
  verificationMode,
  onVerificationModeChange,
  pockets,
  loadingPockets,
  pocketSearch,
  onPocketSearchChange,
  searchPockets,
  searchTenants,
  searchMeters,
  searchBuildings,
  setSearchPockets,
  setSearchTenants,
  setSearchMeters,
  setSearchBuildings,
  filteredPockets,
  groupedArr,
  matchByPocket,
  selectedPocketIds,
  onTogglePocket,
  onToggleAllPockets,
  onToggleGroup,
  totalSelectedMeters,
  caOutOfSync,
  autoIncludeCa,
  onAutoIncludeCaChange,
  periodStart,
  onPeriodStartChange,
  periodEnd,
  onPeriodEndChange,
  partialMonthProrata,
  onPartialMonthProrataChange,
  emailOnFinalize,
  onEmailOnFinalizeChange,
  emailRecipient,
  onEmailRecipientChange,
  extracting,
  extractionProgress,
  reviewStep,
  verificationBill,
  onReupload,
  fileInputRef,
  onFileSelected,
}: CreateBillRunDialogProps) {
  // TV-3 — would this selection skip meters? Asked here rather than threaded
  // down as props from BillRuns.tsx: the Create button that has to be disabled
  // lives in this component's footer, so keeping the question next to it means
  // no new prop surface and no second component that has to remember to pass it
  // through. See useCoverageGuard for the block/permit decisions.
  const coverageGuard = useCoverageGuard({
    enabled: open,
    selectedPocketIds,
    periodStart,
    periodEnd,
  });

  const periodEndHintId = useId();

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title={verificationMode ? 'Create Verification Run' : 'Create Bill Run'}
      description="Select billing pockets and a period to queue for calculation, optionally checked against an uploaded council bill."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            // `coverageGuard.blocked` is only ever true on a real verdict for
            // the CURRENT selection and period — never while a check is in
            // flight, and never when the check failed. A pre-flight check that
            // blocked on "not answered yet" could stop billing outright.
            disabled={
              creating ||
              selectedPocketIds.size === 0 ||
              (verificationMode && !verificationBill) ||
              coverageGuard.blocked
            }
          >
            {creating && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {verificationMode ? 'Confirm & Create Verification Run' : 'Create Bill Run'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Billing Pocket Selection */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label>Billing Pockets *</Label>
            {pockets.length > 0 && (
              <button type="button" onClick={onToggleAllPockets} className="text-xs text-accent hover:opacity-90">
                {selectedPocketIds.size === filteredPockets.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>
          {/* Loading/empty shells: border-only, no bg/shadow — doesn't match
              the Card-adoption trigger (a bg-white/shadow/border-gray shell),
              same reasoning group C used to decline Card where the trigger
              didn't fit. Left as plain bordered divs, just token-ified. */}
          {loadingPockets ? (
            <div className="border border-border-token rounded-lg p-4">
              <div className="animate-pulse space-y-2">
                {/* bg-gray-200 -> bg-border-token, matching the skeleton-bar
                    convention already established in BillRuns.tsx/Pockets.tsx/
                    AuditLogs.tsx. */}
                {[1, 2].map(i => <div key={i} className="h-5 bg-border-token rounded" />)}
              </div>
            </div>
          ) : pockets.length === 0 ? (
            <div className="border border-border-token rounded-lg p-4 text-center">
              <p className="text-content-muted text-sm">No billing pockets found</p>
              <Link to="/pockets" className="text-accent text-sm hover:opacity-90 mt-1 inline-block">
                Create a pocket first
              </Link>
            </div>
          ) : (
            <>
              <Input
                type="text"
                placeholder="Search by pocket, tenant (active or past), or meter..."
                value={pocketSearch}
                onChange={(e) => onPocketSearchChange(e.target.value)}
                className="mb-2"
              />
              <div className="flex items-center gap-2 mb-2 text-xs flex-wrap">
                <span className="text-content-muted">Match:</span>
                {([
                  ['Buildings', searchBuildings, setSearchBuildings],
                  ['Pockets',   searchPockets,   setSearchPockets],
                  ['Tenants',   searchTenants,   setSearchTenants],
                  ['Meters',    searchMeters,    setSearchMeters],
                ] as const).map(([label, on, setOn]) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setOn(!on)}
                    // Active state reuses Badge's own tested 'success' (teal)
                    // tone hexes directly rather than routing through
                    // <Badge>: this is an interactive toggle button (onClick,
                    // active/inactive), not a static status pill, so it
                    // doesn't match the Badge-adoption trigger — but the
                    // colour pairing is the same already-AA-verified teal
                    // 100/900 pair Badge's 'success' tone uses (see
                    // src/test/contrast.test.ts), reused instead of hand-
                    // picking a new one. Inactive state: bg-gray-100/
                    // text-gray-500/border-gray-200 -> bg-surface-sunken/
                    // text-content-muted/border-border-token (this chip reads
                    // as a small recessed panel, not a divider/track).
                    className={`px-2 py-1 rounded-full transition-colors ${
                      on
                        ? 'bg-teal-100 text-teal-900 dark:bg-teal-900 dark:text-teal-100 border border-teal-200 dark:border-teal-800'
                        : 'bg-surface-sunken text-content-muted border border-border-token'
                    }`}
                  >
                    {on ? '✓ ' : ''}{label}
                  </button>
                ))}
              </div>
              <div className="border border-border-token rounded-lg max-h-72 overflow-y-auto">
                {filteredPockets.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-content-muted text-center">No pockets match "{pocketSearch}"</div>
                ) : (
                  groupedArr.map(([buildingKey, group]) => {
                    const groupIds = group.rows.map(r => r.id);
                    const allSelected = groupIds.every(id => selectedPocketIds.has(id));
                    const someSelected = !allSelected && groupIds.some(id => selectedPocketIds.has(id));
                    return (
                      <div key={buildingKey} className="border-b border-border-token last:border-b-0">
                        <div className="flex items-center justify-between px-4 py-2 bg-surface-sunken sticky top-0 z-10">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="font-semibold text-content">{group.name}</span>
                            <span className="text-content-muted">·</span>
                            <span className="text-content-muted">{group.rows.length} pocket{group.rows.length === 1 ? '' : 's'}</span>
                          </div>
                          <button type="button" onClick={() => onToggleGroup(groupIds, allSelected)} className="text-xs text-accent hover:opacity-90">
                            {allSelected ? 'Deselect all' : (someSelected ? 'Select all' : 'Select all in building')}
                          </button>
                        </div>
                        {group.rows.map(pocket => (
                          <label
                            key={pocket.id}
                            // Selected wash routed through the same
                            // `bg-row-selected` token DataTable uses for its
                            // selected-row state (see ui/DataTable.tsx) rather
                            // than the old literal bg-primary-50, so a
                            // selected pocket row reads consistently with a
                            // selected table row elsewhere in the app.
                            className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer border-b border-border-token last:border-b-0 transition-colors ${
                              selectedPocketIds.has(pocket.id) ? 'bg-row-selected' : 'hover:bg-surface-sunken'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedPocketIds.has(pocket.id)}
                              onChange={() => onTogglePocket(pocket.id)}
                              className="w-4 h-4 text-accent border-border-token rounded focus:ring-primary-500"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-content truncate flex items-center gap-2">
                                {pocket.name}
                                {pocket.is_ca && (
                                  <Badge tone="purple" className="uppercase tracking-wide">
                                    Supply
                                  </Badge>
                                )}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                {/* Occupied/vacant indicator routed through
                                    Badge (success/warning tones), dot kept as
                                    a Badge child — same treatment Pockets.tsx
                                    already established for this exact
                                    signal (green -> success/teal hue
                                    normalization included). */}
                                {!pocket.is_ca && (
                                  <Badge tone={pocket.is_occupied ? 'success' : 'warning'} className="gap-1">
                                    <span className={`w-1.5 h-1.5 rounded-full ${
                                      pocket.is_occupied ? 'bg-teal-600 dark:bg-teal-300' : 'bg-amber-600 dark:bg-amber-300'
                                    }`} />
                                    {pocket.is_occupied ? (pocket.active_tenant_name ?? 'Occupied') : 'Vacant'}
                                  </Badge>
                                )}
                                {matchByPocket.get(pocket.id) && (
                                  // text-blue-600 alone is 2.94:1 against the
                                  // dark card — fails. dark:text-blue-400
                                  // (5.97:1, computed by hand) restores it.
                                  <span className="text-xs text-blue-600 dark:text-blue-400 italic" title={`Matched via ${matchByPocket.get(pocket.id)!.kind}`}>
                                    via {matchByPocket.get(pocket.id)!.label}
                                  </span>
                                )}
                                {pocket.shop_no && (
                                  <span className="text-xs text-content-muted">Shop {pocket.shop_no}</span>
                                )}
                                <span className="text-xs text-content-muted">
                                  {pocket.meter_count ?? 0} meter{pocket.meter_count !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
          {selectedPocketIds.size > 0 && (
            <p className="mt-2 text-xs text-accent font-medium">
              {selectedPocketIds.size} pocket{selectedPocketIds.size !== 1 ? 's' : ''} selected ({totalSelectedMeters} meters)
            </p>
          )}

          {/* TV-3 coverage guard. Sits directly under the "N pockets selected
              (M meters)" line because that is the line it qualifies: it says
              how many of those meters would actually be billed.

              A CLEAN SELECTION RENDERS NOTHING — no summary, no spinner, no
              placeholder. `coverage` is null unless the endpoint reported at
              least one blocked meter, so the common case is byte-identical to
              the dialog before this change. */}
          {coverageGuard.coverage !== null && (
            <CoverageBlockNotice
              coverage={coverageGuard.coverage}
              rechecking={coverageGuard.rechecking}
            />
          )}
          {coverageGuard.unavailable !== null && (
            <CoverageUnavailableNotice
              message={coverageGuard.unavailable}
              onRetry={coverageGuard.retry}
            />
          )}

          {/* CA out-of-sync warning. Triggers when a tenant pocket is
              selected but the building's CA pocket has no finalized
              bill_run covering the requested period. The default
              safety net is to also queue a CA Bill Update for the
              same period; user can uncheck to override.
              Fix-wave B: was a self-contained literal amber box with no
              dark: counterpart (the exact "un-dark'd status fill" gap this
              wave closes) — routed through ui/Alert now that Alert exists,
              same tone/shape as BillVerification.tsx's amber ribbon. */}
          {caOutOfSync.length > 0 && (
            <Alert tone="warning" className="mt-3">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="font-semibold mb-1">
                    Common-area bill out of sync for the selected period
                  </p>
                  <ul className="text-xs space-y-0.5 mb-2">
                    {caOutOfSync.map(w => (
                      <li key={w.caPocket.id}>
                        <span className="font-medium">{w.buildingName}</span>
                        {' — '}
                        {w.latestPeriod
                          ? `last CA run was ${w.latestPeriod.start} → ${w.latestPeriod.end}`
                          : 'no CA bill run on record'}
                      </li>
                    ))}
                  </ul>
                  <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoIncludeCa}
                      onChange={e => onAutoIncludeCaChange(e.target.checked)}
                      className="w-3.5 h-3.5 text-amber-700 border-amber-400 rounded focus:ring-amber-500"
                    />
                    <span>
                      Also run <span className="font-semibold">CA Bill Update</span> for {caOutOfSync.length === 1 ? 'this building' : `these ${caOutOfSync.length} buildings`}
                      <span className="opacity-80"> (recommended)</span>
                    </span>
                  </label>
                </div>
              </div>
            </Alert>
          )}
        </div>

        {/* Period */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Period Start</Label>
            <Input type="date" value={periodStart} onChange={(e) => onPeriodStartChange(e.target.value)} />
          </div>
          <div>
            <Label>Period End</Label>
            {/* period_end is EXCLUSIVE (CLAUDE.md §7.2): bill runs are listed
                1st to 1st. Visible line for touch, title for a mouse. */}
            <Input
              type="date"
              value={periodEnd}
              onChange={(e) => onPeriodEndChange(e.target.value)}
              aria-describedby={periodEndHintId}
              title="Bill runs are listed 1st to 1st. The period runs from 00:00 on the start date to 23:59:59 the night before the end date, so 1 Sep to 1 Oct bills 1 Sep 00:00 to 30 Sep 23:59:59."
            />
            <p id={periodEndHintId} className="text-xs text-content-muted mt-1">
              1st of the next month: 1 Sep → 1 Oct bills all of September
            </p>
          </div>
        </div>

        {/* Partial-month toggle — PR 1d / Eskom Schedule §6.
            Default off (calendar months bill the full monthly amount,
            regardless of 28/30/31 day length). Only flip for true
            partial-month bills (new connection mid-month or termination). */}
        <div className="pt-2 border-t border-border-token">
          <label
            className="flex items-center gap-3 cursor-pointer"
            title="Eskom Schedule §6: only set this for connection / termination bills. Normal monthly bills should leave it off."
          >
            {/* Toggle track/knob: the ON colour (amber-500 here; primary-600
                and indigo-600 on the two toggles below) is a fixed literal —
                not a CSS-var token — so it renders identically in both
                themes, same rationale as Button's `warning` fill staying
                fixed across themes. That's deliberate: converting it to a
                theme-varying token (e.g. bg-accent, whose dark value is a
                bright #2dd4bf) would put the white knob on an almost-as-light
                background in dark mode and make it nearly disappear
                (computed contrast ~1.86:1) — so the ON fill stays literal on
                purpose. Only the OFF track (was bg-gray-300, a fixed stone
                literal) moves to bg-border-token, since white-on-border-token
                is high contrast in both themes (10.27:1 dark, computed by
                hand). The knob itself stays literal bg-white: it's a small
                self-contained circle whose contrast pair is with its own
                track, not the page, same reasoning as the notification-count
                bubbles elsewhere in this sweep. */}
            <div className="relative">
              <input
                type="checkbox"
                checked={partialMonthProrata}
                onChange={(e) => onPartialMonthProrataChange(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-10 h-6 rounded-full transition-colors ${partialMonthProrata ? 'bg-amber-500' : 'bg-border-token'}`}>
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${partialMonthProrata ? 'translate-x-4' : ''}`} />
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-content">Partial-month bill (connection / termination)</p>
              <p className="text-xs text-content-muted">
                Eskom Schedule §6: when on, R/kVA/month charges multiply by <code>billing_days / 30</code>.
                Leave off for normal monthly cycles.
              </p>
            </div>
          </label>
        </div>

        {/* Email on Finalize Toggle */}
        <div className="pt-2 border-t border-border-token">
          <label className="flex items-center gap-3 cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                checked={emailOnFinalize}
                onChange={(e) => onEmailOnFinalizeChange(e.target.checked)}
                className="sr-only"
              />
              {/* bg-primary-600 left literal (see the toggle comment above):
                  it's already a fixed, non-theme-varying colour in this
                  config (tailwind.config.js's `primary` ramp is a static
                  object, not a CSS var), so it doesn't need converting to
                  bg-accent — doing so would break the white-knob contrast in
                  dark mode. */}
              <div className={`w-10 h-6 rounded-full transition-colors ${emailOnFinalize ? 'bg-primary-600' : 'bg-border-token'}`}>
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${emailOnFinalize ? 'translate-x-4' : ''}`} />
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-content">Email bills on finalization</p>
              <p className="text-xs text-content-muted">Automatically send bills via email when finalized</p>
            </div>
          </label>

          {emailOnFinalize && (
            <div className="mt-3">
              <Label>Recipient Email</Label>
              <Input
                type="email"
                value={emailRecipient}
                onChange={(e) => onEmailRecipientChange(e.target.value)}
                placeholder="billing@example.com"
              />
            </div>
          )}
        </div>

        {/* Bill Verification Toggle */}
        <div className="pt-2 border-t border-border-token">
          <label className="flex items-center gap-3 cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                checked={verificationMode}
                onChange={(e) => onVerificationModeChange(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-10 h-6 rounded-full transition-colors ${verificationMode ? 'bg-indigo-600' : 'bg-border-token'}`}>
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${verificationMode ? 'translate-x-4' : ''}`} />
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-content">Bill Verification</p>
              <p className="text-xs text-content-muted">Compare calculated bills against a council/municipal bill</p>
            </div>
          </label>

          {verificationMode && !reviewStep && (
            <div className="mt-3">
              {/* Extracting/dropzone: indigo + blue literals below are left
                  untouched on purpose, matching BillVerification.tsx (wave
                  2b group D) — this dialog renders the same upload/review
                  flow as that already-migrated page, and group D explicitly
                  deferred its indigo-hued controls rather than "guess" at a
                  treatment, precisely because this file (BillRuns.tsx's
                  extracted dialog) also uses them. Diverging the two now
                  would make the twin flows look inconsistent; normalizing
                  both together is left as a follow-up.
                  Final tidy: that reasoning no longer covers the "Re-upload"
                  button in the reviewStep branch below — its
                  text-indigo-600/hover:text-indigo-800 failed AA outright in
                  dark mode (2.41:1 / 1.53:1), so both this dialog's and
                  BillVerification.tsx's Re-upload buttons were themed
                  together (dark:text-indigo-400 / dark:hover:text-indigo-300),
                  keeping the twin flows identical to each other rather than
                  to their old untouched state. The dropzone/checkbox indigo
                  literals immediately below remain deferred. */}
              {extracting ? (
                <Alert tone="info">
                  <div className="flex items-center gap-3">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>{extractionProgress}</span>
                  </div>
                </Alert>
              ) : (
                // C6 (dark-mode contrast fix): hover:bg-indigo-50 had no
                // dark: counterpart (1.07:1 collapse), same site as
                // BillVerification.tsx's twin upload dropzone (C5). Same fix,
                // same reasoning: dark:hover:bg-indigo-900 — text-content
                // 10.94:1 / text-content-muted 4.53:1 against it (both clear
                // AA). indigo-950 was rejected — ~1.05:1 against this
                // dialog's surfaceRaised(dark) body, functionally invisible;
                // indigo-900 stays visible at 1.33:1. See
                // src/test/contrast.test.ts.
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-border-token rounded-lg cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900 transition-colors"
                >
                  <svg className="w-8 h-8 text-content-muted mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm font-medium text-content">Upload council bill PDF</p>
                  <p className="text-xs text-content-muted mt-1">Eskom, Drakenstein, City of Cape Town, etc. (max 10MB)</p>
                  {/* Raw <input> intentionally kept: it needs a real DOM ref
                      (fileInputRef.current?.click() above) to work as a
                      hidden trigger for the visible dropzone, and the shared
                      Input component is a plain function component (no
                      forwardRef) so it cannot accept a ref. */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onFileSelected(f);
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {verificationMode && reviewStep && verificationBill && (
            <div className="mt-3 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-content">Review Extracted Values</h4>
                <button onClick={onReupload} className="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300">
                  Re-upload
                </button>
              </div>
              <Alert tone="warning">
                Please verify these values match the council bill. AI extraction can make mistakes - you are responsible for confirming accuracy.
              </Alert>

              {/* Bill Header — recessed sub-panel (bg-surface-sunken), not
                  Card: these are inset summary blocks inside an already-
                  bg-surface-raised dialog body, matching BillVerification.tsx's
                  identical review-step panels (also bg-surface-sunken, not
                  Card) rather than a second raised shell nested inside the
                  first. */}
              <div className="bg-surface-sunken rounded-lg p-3 space-y-2">
                <p className="text-xs font-semibold text-content-muted uppercase">Bill Info</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-content-muted">Municipality:</span> <span className="font-medium">{verificationBill.municipality_name || '-'}</span></div>
                  <div><span className="text-content-muted">Account:</span> <span className="font-medium">{verificationBill.account_number || '-'}</span></div>
                  <div><span className="text-content-muted">Invoice:</span> <span className="font-medium">{verificationBill.invoice_number || '-'}</span></div>
                  <div><span className="text-content-muted">Period:</span> <span className="font-medium">{verificationBill.billing_period_start} to {verificationBill.billing_period_end}</span></div>
                  <div><span className="text-content-muted">Billing Days:</span> <span className="font-medium">{verificationBill.billing_days || '-'}</span></div>
                  <div><span className="text-content-muted">Deposit:</span> <span className="font-medium">{verificationBill.deposit_cents ? `R ${(verificationBill.deposit_cents / 100).toFixed(2)}` : '-'}</span></div>
                </div>
              </div>

              {/* Meters — fixed-shape summary table, not list data; kept as
                  a plain <table> per the task-12 brief (do not migrate to
                  DataTable). */}
              {verificationBill.meters && verificationBill.meters.length > 0 && (
                <div className="bg-surface-sunken rounded-lg p-3 space-y-2">
                  <p className="text-xs font-semibold text-content-muted uppercase">Meters ({verificationBill.meters.length})</p>
                  <div className="overflow-x-auto">
                    {/* 6 columns in a dialog sheet nested one card deeper than
                        the page itself — min-w-max forces natural width so
                        the overflow-x-auto wrapper actually scrolls instead
                        of every column squeezing into a sliver (see
                        DataTable.tsx's min-w-max comment). */}
                    <table className="w-full text-xs min-w-max">
                      <thead>
                        <tr className="text-content-muted">
                          <th className="text-left py-1 pr-2">Meter</th>
                          <th className="text-left py-1 pr-2">Tariff</th>
                          <th className="text-right py-1 pr-2">Prev</th>
                          <th className="text-right py-1 pr-2">Curr</th>
                          <th className="text-right py-1 pr-2">Units</th>
                          <th className="text-right py-1">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {verificationBill.meters.map((m) => (
                          <tr key={m.id} className="border-t border-border-token">
                            <td className="py-1 pr-2 font-medium">{m.meter_number || '-'}</td>
                            <td className="py-1 pr-2">{m.tariff_code || '-'}</td>
                            <td className="py-1 pr-2 text-right">{m.previous_reading?.toLocaleString() ?? '-'}</td>
                            <td className="py-1 pr-2 text-right">{m.current_reading?.toLocaleString() ?? '-'}</td>
                            <td className="py-1 pr-2 text-right">{m.units?.toLocaleString() ?? '-'}</td>
                            <td className="py-1 text-right font-medium">{m.amount_cents ? `R ${(m.amount_cents / 100).toFixed(2)}` : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Line Items — same as above: fixed-shape summary, kept as a
                  plain <table>. */}
              {verificationBill.line_items && verificationBill.line_items.length > 0 && (
                <div className="bg-surface-sunken rounded-lg p-3 space-y-2">
                  <p className="text-xs font-semibold text-content-muted uppercase">Charge Line Items ({verificationBill.line_items.length})</p>
                  <div className="overflow-x-auto">
                    {/* Description is unbounded free text from OCR-extracted
                        council-bill line items (not a short enum), in a
                        dialog sheet nested one card deeper than the page —
                        min-w-max forces natural width so overflow-x-auto
                        actually scrolls instead of Description wrapping into
                        a sliver (see DataTable.tsx's min-w-max comment). */}
                    <table className="w-full text-xs min-w-max">
                      <thead>
                        <tr className="text-content-muted">
                          <th className="text-left py-1 pr-2">Description</th>
                          <th className="text-right py-1 pr-2">Qty</th>
                          <th className="text-right py-1 pr-2">Rate</th>
                          <th className="text-right py-1">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {verificationBill.line_items.map((li) => (
                          <tr key={li.id} className="border-t border-border-token">
                            <td className="py-1 pr-2">{li.description || li.charge_type}</td>
                            <td className="py-1 pr-2 text-right">{li.quantity?.toLocaleString() ?? '-'}</td>
                            <td className="py-1 pr-2 text-right">{li.rate_cents ? `R ${(li.rate_cents / 100).toFixed(4)}` : '-'}</td>
                            <td className="py-1 text-right font-medium">R {(li.amount_cents / 100).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Totals */}
              <div className="bg-surface-sunken rounded-lg p-3">
                <div className="flex justify-between text-sm">
                  <span className="text-content-muted">Electricity Total:</span>
                  <span className="font-medium">{verificationBill.electricity_total_cents ? `R ${(verificationBill.electricity_total_cents / 100).toFixed(2)}` : '-'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-content-muted">VAT (15%):</span>
                  <span className="font-medium">{verificationBill.vat_cents ? `R ${(verificationBill.vat_cents / 100).toFixed(2)}` : '-'}</span>
                </div>
                <div className="flex justify-between text-sm font-bold border-t border-border-token pt-1 mt-1">
                  <span>Total Amount:</span>
                  <span>{verificationBill.total_amount_cents ? `R ${(verificationBill.total_amount_cents / 100).toFixed(2)}` : '-'}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <Alert tone="danger" className="mt-4">
          {error}
        </Alert>
      )}
    </ResponsiveDialog>
  );
}
